(() => {
  const MOBILE_QUERY = '(max-width: 767px)';
  const CARD_SELECTOR = '.template-collection .m-product-card__media';
  const initialized = new WeakSet();
  let observer;

  const chevron = (direction) => direction === 'prev'
    ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
    : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function createControls(media) {
    if (initialized.has(media)) return;
    initialized.add(media);

    const link = media.querySelector('.m-product-card__link[href]');
    const image = media.querySelector('.m-product-card__main-image img');
    if (!link || !image) return;

    const controls = document.createElement('div');
    controls.className = 'ra-card-gallery-controls';
    controls.innerHTML = `
      <button type="button" class="ra-card-gallery-arrow ra-card-gallery-arrow--prev" data-ra-gallery-arrow="prev" aria-label="Immagine precedente" disabled>${chevron('prev')}</button>
      <button type="button" class="ra-card-gallery-arrow ra-card-gallery-arrow--next" data-ra-gallery-arrow="next" aria-label="Immagine successiva">${chevron('next')}</button>
    `;
    media.appendChild(controls);

    media._raGallery = {
      link,
      image,
      main: media.querySelector('.m-product-card__main-image'),
      controls,
      prev: controls.querySelector('[data-ra-gallery-arrow="prev"]'),
      next: controls.querySelector('[data-ra-gallery-arrow="next"]'),
      images: null,
      index: 0,
      displayedIndex: 0,
      requestVersion: 0,
      loading: false,
      original: {
        src: image.currentSrc || image.src,
        srcset: image.getAttribute('srcset') || '',
        sizes: image.getAttribute('sizes') || '',
        alt: image.alt || ''
      }
    };

    observer.observe(media);
  }

  async function loadImages(media) {
    const state = media._raGallery;
    if (!state || state.images) return state?.images || [];
    if (state.pendingImages) return state.pendingImages;
    state.loading = true;
    state.pendingImages = (async () => {

    try {
      const productUrl = new URL(state.link.href, window.location.origin);
      productUrl.search = '';
      productUrl.hash = '';
      productUrl.pathname = productUrl.pathname.replace(/\/$/, '') + '.js';

      const response = await fetch(productUrl.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Impossibile caricare le immagini');

      const product = await response.json();
      const urls = Array.isArray(product.images) ? product.images.filter(Boolean) : [];
      state.images = urls.length ? urls : [state.original.src];

      if (state.images.length > 1) {
        media.classList.add('ra-gallery-ready');
        updateButtons(state);
      } else {
        state.controls.remove();
      }
    } catch (error) {
      // A temporary network failure must leave the arrows available for retry.
      return [];
    } finally {
      state.loading = false;
      state.pendingImages = null;
    }

    return state.images;
    })();
    return state.pendingImages;
  }

  function updateButtons(state) {
    state.prev.disabled = state.index <= 0;
    state.next.disabled = state.index >= state.images.length - 1;
  }

  function showImage(media, newIndex) {
    const state = media._raGallery;
    if (!state || !state.images?.length) return;

    state.index = Math.max(0, Math.min(newIndex, state.images.length - 1));
    const targetIndex = state.index;
    const requestVersion = ++state.requestVersion;
    const isOriginal = state.index === 0;
    const nextSrc = isOriginal ? state.original.src : sizedImageUrl(state.images[state.index], media);

    media.classList.add('ra-gallery-active');
    state.main?.classList.add('ra-gallery-changing');

    const preloader = new Image();
    preloader.onload = () => {
      if (requestVersion !== state.requestVersion) return;
      if (isOriginal) {
        state.image.src = state.original.src;
        if (state.original.srcset) state.image.setAttribute('srcset', state.original.srcset);
        else state.image.removeAttribute('srcset');
        if (state.original.sizes) state.image.setAttribute('sizes', state.original.sizes);
        else state.image.removeAttribute('sizes');
        state.image.alt = state.original.alt;
      } else {
        state.image.removeAttribute('srcset');
        state.image.removeAttribute('sizes');
        state.image.src = nextSrc;
      }
      state.displayedIndex = targetIndex;
      state.main?.classList.remove('ra-gallery-changing');
    };
    preloader.onerror = () => {
      if (requestVersion !== state.requestVersion) return;
      state.index = state.displayedIndex;
      state.main?.classList.remove('ra-gallery-changing');
      updateButtons(state);
    };
    preloader.src = nextSrc;

    updateButtons(state);
  }

  function sizedImageUrl(src, media) {
    const url = new URL(src, window.location.origin);
    if (url.hostname === 'cdn.shopify.com' || (url.origin === window.location.origin && url.pathname.startsWith('/cdn/shop/'))) {
      const cssWidth = media.getBoundingClientRect().width || 360;
      const width = Math.min(1600, Math.max(360, Math.ceil(cssWidth * Math.min(window.devicePixelRatio || 1, 3) / 100) * 100));
      url.searchParams.set('width', String(width));
    }
    return url.toString();
  }

  async function onArrowClick(event) {
    const button = event.target.closest('[data-ra-gallery-arrow]');
    if (!button || !window.matchMedia(MOBILE_QUERY).matches) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const media = button.closest('.m-product-card__media');
    if (!media) return;
    const images = await loadImages(media);
    if (images.length < 2) return;

    const direction = button.dataset.raGalleryArrow;
    showImage(media, media._raGallery.index + (direction === 'next' ? 1 : -1));
  }

  function scan(root = document) {
    if (!window.matchMedia(MOBILE_QUERY).matches) return;
    root.querySelectorAll(CARD_SELECTOR).forEach(createControls);
  }

  function start() {
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        loadImages(entry.target);
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '300px 0px' });

    scan();
    document.addEventListener('click', onArrowClick, true);

    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches?.(CARD_SELECTOR)) createControls(node);
          scan(node);
        }
      }
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
